import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Building2, Loader2, TriangleAlert } from "lucide-react"
import { z } from "zod"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { PaisSoportado } from "@/routes/masters/api"
import { t } from "@/lib/i18n"

const schema = z.object({
  razon_social: z.string().trim().min(2, "La razón social es obligatoria."),
  tax_id: z
    .string()
    .trim()
    .min(3, "El identificador fiscal es obligatorio.")
    .max(30, "Como mucho 30 caracteres."),
  pais: z.string().min(2, "Elige el país: decide el rulepack y no se puede cambiar después."),
  moneda_funcional: z
    .string()
    .trim()
    .length(3, "Tres letras: PEN, MXN, BRL, USD…"),
  regimen_tributario: z.string().trim(),
  motivo: z.string().trim().min(3, "El motivo es obligatorio: queda en la pista de auditoría."),
})

export type SociedadFormValues = z.infer<typeof schema>

const VACIO: SociedadFormValues = {
  razon_social: "",
  tax_id: "",
  pais: "",
  moneda_funcional: "",
  regimen_tributario: "",
  motivo: "",
}

/** Cómo se lee la madurez de un rulepack. Existir y estar completo no es lo mismo. */
const MADUREZ: Record<string, string> = {
  COMPLETO: "Genera declaraciones: formularios y libros electrónicos incluidos.",
  MODELO:
    "Normativa, calendario y planilla, PERO sin formularios: el entregable se prepara fuera del sistema.",
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  paises: PaisSoportado[]
  paisesCargando?: boolean
  pending?: boolean
  onSubmit: (values: SociedadFormValues) => void | Promise<void>
}

/**
 * ALTA DE SOCIEDAD. Es lo primero de cualquier onboarding.
 *
 * Hasta ahora las sociedades solo entraban por `seeds/sociedades.py`, un script
 * con las cuatro de ESTE cliente escritas dentro: un cliente nuevo no podia ni
 * empezar, porque sin sociedad no hay libros, ni periodos, ni comprobantes.
 *
 * EL PAIS ES EL CAMPO CONSECUENTE, y por eso es un desplegable que sale de
 * `/paises` y no un campo de texto. Decide el rulepack, el plan de cuentas y el
 * calendario, y el backend NO LO DEJA CAMBIAR despues —cambiarlo dejaria los
 * asientos ya resueltos contra una normativa que no aplica—, asi que se dice
 * aqui, antes, y no en un 422 despues. La madurez del rulepack va al lado por
 * lo mismo: quien da de alta una sociedad mexicana tiene que saber que ese pais
 * no genera declaraciones ANTES del cierre, no durante.
 *
 * El regimen se puede dejar vacio y declararlo despues desde la tabla. Lo que
 * NO se hace es rellenarlo con una suposicion: el motor esta escrito para ir
 * corto cuando no sabe, y un regimen inventado deja el onboarding en verde con
 * el calculo yendo por el general.
 */
export function SociedadFormDialog({
  open,
  onOpenChange,
  paises,
  paisesCargando,
  pending,
  onSubmit,
}: Props) {
  const form = useForm<SociedadFormValues>({
    resolver: zodResolver(schema),
    defaultValues: VACIO,
  })

  React.useEffect(() => {
    if (open) form.reset(VACIO)
  }, [open, form])

  const pais = form.watch("pais")
  const elegido = paises.find((p) => p.pais === pais) ?? null

  // La moneda se PROPONE del rulepack y se puede cambiar: una sociedad
  // panameña que lleve sus libros en dólares y una que los lleve en balboas son
  // las dos posibles, y adivinarlo sin dejar corregirlo sería peor.
  React.useEffect(() => {
    if (elegido?.moneda && !form.getValues("moneda_funcional")) {
      form.setValue("moneda_funcional", elegido.moneda, { shouldValidate: true })
    }
  }, [elegido, form])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Alta de sociedad")}</DialogTitle>
          <DialogDescription>
            {t("Se crean también sus tres libros (statutory, tax y management) en la misma operación. Sin ellos la sociedad se ve normal en la lista y falla en el primer comprobante.")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              await onSubmit(values)
            })}
            className="space-y-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="razon_social"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>{t("Razón social *")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("SIETE AGENCY S.A.C.")} {...field} />
                    </FormControl>
                    <FormDescription>
                      {t("Tal como figura ante el fisco. Si no coincide con la del maestro, al declarar se usa la fiscal.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tax_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Identificador fiscal *")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("RUC, RFC, CNPJ…")} className="code" {...field} />
                    </FormControl>
                    <FormDescription>
                      {t("Se compara sin guiones ni puntos: el mismo identificador escrito de dos formas no puede partir una contabilidad en dos.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pais"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("País *")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={paisesCargando ? "Cargando…" : "Elige el país"}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paises.map((p) => (
                          <SelectItem key={p.pais} value={p.pais}>
                            {p.pais}
                            {p.madurez ? ` · ${p.madurez.toLowerCase()}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t("Decide el rulepack, el plan de cuentas y el calendario.")}{" "}
                      <strong className="font-medium">{t("No se puede cambiar después.")}</strong>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="moneda_funcional"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Moneda funcional *")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("PEN")}
                        maxLength={3}
                        className="code"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormDescription>
                      {elegido?.moneda
                        ? `El rulepack de ${elegido.pais} usa ${elegido.moneda}. Cámbiala si esta sociedad lleva sus libros en otra.`
                        : "La moneda en la que esta sociedad lleva sus libros."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="regimen_tributario"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Régimen tributario")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("Opcional")}
                        className="code"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormDescription>
                      {t("Se puede dejar vacío y declararlo después desde la tabla, donde el desplegable sale del rulepack y se valida. Aquí no se valida: escribirlo mal deja el onboarding en verde con el cálculo yendo por el general.")}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {elegido ? (
              <div className="flex flex-wrap items-start gap-2 rounded-md border bg-surface-sunken/50 px-2.5 py-2">
                <Badge variant={elegido.genera_declaraciones ? "success-soft" : "warning-soft"} estado>
                  {elegido.pais} · {elegido.madurez?.toLowerCase()}
                </Badge>
                <p className="min-w-0 flex-1 text-2xs leading-4 text-muted-foreground">
                  {MADUREZ[elegido.madurez] ?? "Madurez del rulepack no declarada."}
                  {elegido.lee_comprobantes === false
                    ? " Además, el sistema todavía no sabe leer los comprobantes de este país: tendrán que entrar a mano o por CSV."
                    : null}
                </p>
              </div>
            ) : null}

            <FormField
              control={form.control}
              name="motivo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Motivo *")}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder={t("Por qué se da de alta. Queda en la pista de auditoría con tu nombre y la fecha.")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!form.watch("regimen_tributario") ? (
              <p className="flex items-start gap-1.5 text-2xs leading-4 text-warning">
                <TriangleAlert className="mt-px h-3 w-3 shrink-0" />
                {t("Sin régimen, el sistema calcula por el general y marca comprobantes para revisión. Es lo correcto mientras el dato no exista — pero es un pendiente, no un estado final.")}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("Cancelar")}</Button>
              <Button type="submit" disabled={pending}>
                {pending ? <Loader2 className="animate-spin" /> : <Building2 />}
                {pending ? "Creando…" : "Crear sociedad"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
